import React, { useEffect, useState, useRef } from 'react';
type Product = {
  title: string;
  priceText: string;
  thumbnailUrl: string;
};
const NAMES = ['James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda', 'William', 'Elizabeth', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica', 'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Nancy', 'Daniel', 'Lisa', 'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra'];
const CITIES = ['New York, NY', 'Los Angeles, CA', 'Chicago, IL', 'Houston, TX', 'Phoenix, AZ', 'Philadelphia, PA', 'San Antonio, TX', 'San Diego, CA', 'Dallas, TX', 'San Jose, CA', 'Austin, TX', 'Jacksonville, FL', 'Fort Worth, TX', 'Columbus, OH', 'Charlotte, NC', 'San Francisco, CA', 'Indianapolis, IN', 'Seattle, WA', 'Denver, CO', 'Washington, DC', 'Boston, MA', 'El Paso, TX', 'Nashville, TN', 'Detroit, MI', 'Oklahoma City, OK'];
const scanDOMForProducts = (): Product[] => {
  const found: Product[] = [];
  const seenTitles = new Set<string>();
  // 1. Try JSON-LD Product Schema
  const scripts = document.querySelectorAll('script[type="application/ld+json"]');
  scripts.forEach(script => {
    try {
      const data = JSON.parse(script.textContent || '');
      if (data['@type'] === 'Product') {
        const title = data.name;
        if (title && !seenTitles.has(title)) {
          found.push({
            title,
            priceText: data.offers?.price ? `$${data.offers.price}` : '',
            thumbnailUrl: data.image || ''
          });
          seenTitles.add(title);
        }
      }
    } catch (e) {
      // Ignore parsing errors
    }
  });
  // 2. Heuristic DOM Scanning (Images near headings and prices)
  const images = Array.from(document.querySelectorAll('img'));
  for (const img of images) {
    let container: HTMLElement | null = img.parentElement;
    let depth = 0;
    // Search up to 5 levels up for a container that looks like a product card
    while (container && depth < 5) {
      const text = container.innerText || '';
      // Look for a price pattern (e.g., $9, $97, $6.36, USD 27.00)
      const priceMatch = text.match(/(?:USD\s)?\$\s?\d+(?:,\d{3})*(?:\.\d{2})?/i);
      if (priceMatch) {
        // Find the closest heading or strong tag
        const heading = container.querySelector('h1, h2, h3, h4, h5, strong');
        let title = heading ? (heading as HTMLElement).innerText.trim() : '';
        // Clean up title
        title = title.replace(/\n/g, ' ').trim();
        // If we found a reasonable title that we haven't seen yet
        if (title && title.length > 3 && title.length < 80 && !seenTitles.has(title)) {
          found.push({
            title,
            priceText: priceMatch[0],
            thumbnailUrl: img.src
          });
          seenTitles.add(title);
          break; // Stop going up the tree for this image
        }
      }
      container = container.parentElement;
      depth++;
    }
  }
  return found;
};
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<{
    name: string;
    city: string;
    product: Product;
  } | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const recentNamesRef = useRef<string[]>([]);
  const productsRef = useRef<Product[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Perform initial scan
    let scannedProducts = scanDOMForProducts();
    // Fallback if no products detected
    if (scannedProducts.length === 0) {
      scannedProducts = [{
        title: 'Pack Despertar / Physical Book',
        priceText: '$27',
        thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/rkyG2UTAdxpsg9UVRM7F7u/THE_10K_IA_CONTENT.png'
      }, {
        title: 'Edición Digital / Digital Edition',
        priceText: '$17',
        thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/rkyG2UTAdxpsg9UVRM7F7u/THE_10K_IA_CONTENT.png'
      }];
    }
    productsRef.current = scannedProducts;
    const showNextToast = () => {
      const products = productsRef.current;
      let selectedProduct: Product;
      // Check if we are using the exact fallback array to apply the 70/30 rule
      if (products.length === 2 && products[0].title.includes('Pack Despertar')) {
        selectedProduct = Math.random() < 0.7 ? products[0] : products[1];
      } else {
        // Otherwise, pick a random product from the scanned list
        selectedProduct = products[Math.floor(Math.random() * products.length)];
      }
      // Pick a random city
      const city = CITIES[Math.floor(Math.random() * CITIES.length)];
      // Pick a random name, avoiding the last 10 used
      let availableNames = NAMES.filter(n => !recentNamesRef.current.includes(n));
      if (availableNames.length === 0) availableNames = NAMES; // Reset if all names used
      const name = availableNames[Math.floor(Math.random() * availableNames.length)];
      // Update recent names history
      recentNamesRef.current = [name, ...recentNamesRef.current].slice(0, 10);
      // Set toast data and show
      setToast({
        name,
        city,
        product: selectedProduct
      });
      setIsVisible(true);
      // Hide after 4 seconds
      setTimeout(() => {
        setIsVisible(false);
        // Schedule next toast between 8 and 15 seconds
        const nextDelay = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
        timeoutRef.current = setTimeout(showNextToast, nextDelay);
      }, 4000);
    };
    // Start the first toast after 3 seconds
    timeoutRef.current = setTimeout(showNextToast, 3000);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  if (!toast) return null;
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-none w-full max-w-md px-4 flex justify-center" data-id="element-638">
      <div className={`bg-dark-900 border border-cyan-500/30 shadow-[0_10px_40px_rgba(0,240,255,0.15)] rounded-full p-2 pr-5 flex items-center gap-3 transform transition-all duration-500 ease-out motion-reduce:transition-none motion-reduce:transform-none ${isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'}`} data-id="element-639">
        {toast.product.thumbnailUrl && <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-12 h-12 rounded-full object-cover border border-cyan-500/50 flex-shrink-0 bg-dark-950" data-id="element-640" />}
        <div className="flex flex-col justify-center py-1" data-id="element-641">
          <p className="text-sm text-gray-300 leading-tight" data-id="element-642">
            <strong className="text-white font-semibold" data-id="element-643">{toast.name}</strong>{' '}
            in{' '}
            <strong className="text-white font-semibold" data-id="element-644">{toast.city}</strong>{' '}
            just purchased
          </p>
          <p className="text-sm leading-tight mt-0.5" data-id="element-645">
            <span className="text-cyan-400 font-medium" data-id="element-646">
              {toast.product.title}
            </span>{' '}
            <span className="text-gray-500" data-id="element-647">— {toast.product.priceText}</span>
          </p>
        </div>
      </div>
    </div>;
}